import { OddsFormat } from '@bpos';
import { Option, OptionMarket, OptionMarketStatus, OptionStatus, ParticipantPriceType, Price } from '@cds/betting-offer';
import { MessageEnvelope } from '@cds/push';
import {
    isFixtureSlimUpdateEnvelope,
    isOptionMarketDeleteEnvelope,
    isOptionMarketUpdateEnvelope,
} from '@frontend/sports/content-distribution/feature';
import { OddsOperations } from '@frontend/sports/odds/feature';

import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { toComboPrevention } from '../../../core/picks/combo-prevention';
import { toMinimumComboTV2 } from '../../../core/picks/minimum-combination';
import { IPrice, PriceType } from '../../../core/picks/pick-models';
import { optionPriceToBetslipPrice } from '../../../core/price';

export class BetslipV2OptionMarketPickUpdater {
    static updateV2OptionMarketPick(pick: BetslipV2OptionMarketPick, message: MessageEnvelope, oddsFormat: OddsFormat): BetslipPick {
        return BetslipV2OptionMarketPickUpdater.updateFromMessage(pick, message, oddsFormat);
    }

    private static updateFromMessage(pick: BetslipV2OptionMarketPick, message: MessageEnvelope, oddsFormat: OddsFormat): BetslipPick {
        if (
            isOptionMarketDeleteEnvelope(message) &&
            message.payload.fixtureId === pick.fixture.fixtureId &&
            message.payload.marketId === pick.market.id
        ) {
            const updatedPick = pick.copy();
            updatedPick.market.isClosed = true;

            return updatedPick;
        }

        if (
            isOptionMarketUpdateEnvelope(message) &&
            message.payload.fixtureId === pick.fixture.fixtureId &&
            message.payload.optionMarket.id === pick.market.id
        ) {
            const updatedPick = pick.copy();
            BetslipV2OptionMarketPickUpdater.updateFromMarket(updatedPick, message.payload.optionMarket, oddsFormat);

            return updatedPick;
        }

        if (isFixtureSlimUpdateEnvelope(message) && message.payload.fixtureId === pick.fixture.fixtureId) {
            const updatedPick = pick.copy();
            updatedPick.fixture.stage = message.payload.stage;

            return updatedPick;
        }

        return pick;
    }

    private static updateFromMarket(pick: BetslipV2OptionMarketPick, market: OptionMarket, oddsFormat: OddsFormat): void {
        pick.market.isVisible = market.status === OptionMarketStatus.Visible;
        pick.market.isClosed = market.status === OptionMarketStatus.Hidden;

        pick.market.comboPrevention = toComboPrevention(market.comboPrevention);
        pick.market.minimumCombo = toMinimumComboTV2(market.minCombo);
        const cdsOption = market.options.find((o) => o.id === pick.option.id);
        BetslipV2OptionMarketPickUpdater.updateFromOption(pick, cdsOption, oddsFormat);
        BetslipV2OptionMarketPickUpdater.updateSiblingFromOption(pick, market);
        pick.market.isFixedPriceAvailable =
            !!market.isWinPriceEnabled &&
            !pick.isFavouritePick &&
            market.options.find(
                (o) =>
                    o.participantPriceType === ParticipantPriceType.Fixed &&
                    cdsOption?.parameters?.fixtureParticipant === o.parameters?.fixtureParticipant,
            )?.price.odds! > 1;
        pick.market.isEachWay = market.isEachWay! && !pick.isFavouritePick;
    }

    private static updateFromOption(pick: BetslipV2OptionMarketPick, fixtureOption: Option | undefined, oddsFormat: OddsFormat): void {
        if (fixtureOption) {
            pick.option.isVisible = fixtureOption.status === OptionStatus.Visible;
            if (pick.option.isVisible && pick.boostedPriceDetails?.isPriceBoosted) {
                pick.option.isVisible = !!fixtureOption.boostedPrice;
            }
            for (const price of pick.prices) {
                if (price.id === fixtureOption.price.id) {
                    BetslipV2OptionMarketPickUpdater.updateFromBoostedPrice(pick, pick.boostedPriceDetails?.boostedPrice, fixtureOption.boostedPrice);
                    BetslipV2OptionMarketPickUpdater.updateFromPrice(pick, price, oddsFormat, fixtureOption.price);
                }
            }
        } else {
            pick.option.isVisible = false;
        }
    }

    private static updateSiblingFromOption(pick: BetslipV2OptionMarketPick, market: OptionMarket): void {
        const siblingOption = market.options.find((o) => o.id === pick.siblingOption?.id);
        if (siblingOption && pick.siblingOption) {
            pick.siblingOption.isVisible = siblingOption.status === OptionStatus.Visible;
            if (pick.siblingOption.isVisible && pick.boostedPriceDetails?.isPriceBoosted) {
                pick.siblingOption.isVisible = !!siblingOption.boostedPrice;
            }
            pick.siblingOption.prices = [optionPriceToBetslipPrice(market, siblingOption.price)];
        }
    }

    private static updateFromPrice(pick: BetslipV2OptionMarketPick, thisPrice: IPrice, oddsFormat: OddsFormat, fixturePrice?: Price): void {
        if (fixturePrice) {
            const newPrice = {
                isVisible: true,
                nativeOdds: OddsOperations.createOdds(fixturePrice),
            };

            newPrice.isVisible =
                newPrice.isVisible && (pick.priceType === PriceType.StartingPrice || OddsOperations.isOddsValid(newPrice.nativeOdds));
            if (!OddsOperations.equal(newPrice.nativeOdds, thisPrice.nativeOdds, oddsFormat)) {
                pick.priceHistory.push({ ...thisPrice });
            }

            thisPrice.isVisible = newPrice.isVisible;
            thisPrice.nativeOdds = newPrice.nativeOdds;
        } else {
            pick.priceHistory.push({ ...thisPrice });
            thisPrice.isVisible = false;
        }
    }

    private static updateFromBoostedPrice(pick: BetslipV2OptionMarketPick, thisBoostedPrice?: IPrice, boostedPrice?: Price): void {
        if (pick.boostedPriceDetails?.isPriceBoosted && boostedPrice && thisBoostedPrice) {
            const newBoostedPrice = {
                isVisible: true,
                nativeOdds: OddsOperations.createOdds(boostedPrice),
            };

            newBoostedPrice.isVisible = newBoostedPrice.isVisible && OddsOperations.isOddsValid(newBoostedPrice.nativeOdds);
            if (!OddsOperations.equal(newBoostedPrice.nativeOdds, thisBoostedPrice.nativeOdds)) {
                pick.boostedPriceDetails.boostedPrice!.nativeOdds = newBoostedPrice.nativeOdds;
                thisBoostedPrice.isVisible = newBoostedPrice.isVisible;
                thisBoostedPrice.nativeOdds = newBoostedPrice.nativeOdds;
            }
        }
    }
}
